// Builds the "Approvals & Sign-offs" cards for an order from its direct
// sign-off fields + status_history. Works for every stage, incl. Fulfilled.
const FULFILLED_LABEL = {
  pickup_ready: "Ready for pick-up",
  shopify_direct: "Fulfilled via Shopify",
  shopify_label: "Shipped · Shopify label",
  shopify_admin_label: "Shipped · Shopify Admin label",
  self_checkout: "Shipped · self checkout",
};

// Email or raw string → friendly display name ("factory1@x.com" → "Factory1").
export function signerName(raw) {
  if (!raw) return "—";
  const s = String(raw).trim();
  if (!s.includes("@")) return s;
  return s.split("@")[0].split(/[._-]+/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

export function formatWhen(iso) {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: String(iso).slice(0, 10), time: String(iso).slice(11, 19) };
  return {
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }),
  };
}

const near = (a, b) => Math.abs(new Date(a || 0).getTime() - new Date(b || 0).getTime()) < 120000;

export function buildSignoffEvents(o, stageLabel = {}) {
  if (!o) return [];
  const events = [];
  const push = (cond, ev) => { if (cond) events.push(ev); };
  // `dedupe: true` = a status_history entry with the same status is the same event → shown once.
  push(o.factory_signature, { key: "start", label: "Production Started", status: "production", signer: o.factory_signature, at: o.factory_signed_at, tone: "default", dedupe: true });
  push(o.production_completed_by, { key: "prod-complete", label: "Production Complete", status: "product_transfer", signer: o.production_completed_by, at: o.production_completed_at, tone: "default", dedupe: true });
  push(o.packing_received_by, { key: "packing-received", label: "Received by Packing", status: "packing", signer: o.packing_received_by, at: o.packing_received_at, tone: "emerald", dedupe: true });
  push(o.packed_by, { key: "packed", label: "Packed & Checked", status: "packed", signer: o.packed_by, at: o.packed_at, tone: "emerald" });
  push(o.inventory_packed_by, { key: "inv-packed", label: "Packed from Inventory", status: "inventory_packed", signer: o.inventory_packed_by, at: o.inventory_packed_at, tone: "emerald" });
  push(o.shipping_tracking_number, { key: "shipped", label: o.status === "fulfilled" ? "Shipped to client" : "In transit to warehouse", status: "shipping", signer: o.shipping_tracking_number, at: o.shipping_shipped_at, extra: o.shipping_eta ? `ETA ${String(o.shipping_eta).slice(0, 10)}` : null, tone: "default", dedupe: true });
  push(o.received_by, { key: "received", label: "Received at warehouse", status: "completed", signer: o.received_by, at: o.received_at, extra: o.bin_number ? `Bin ${o.bin_number}` : null, tone: "default", dedupe: true });
  push(o.fulfilled_by, { key: "fulfilled", label: FULFILLED_LABEL[o.fulfilled_method] || "Order Fulfilled", status: "fulfilled", signer: o.fulfilled_by, at: o.fulfilled_at, tone: "emerald" });
  push(o.customer_fit_confirmed_at, { key: "fit-confirmed", label: "Fit confirmed by customer", status: "fit_confirmed", signer: o.customer_fit_confirmed_by || "Customer", at: o.customer_fit_confirmed_at,
    extra: o.customer_fit_confirmed_via === "photos" ? "Shared photos of the installed covers" : "Clicked the review link", tone: "emerald" });
  push(o.cancelled_by, { key: "cancelled", label: "Order Cancelled", status: "cancelled", signer: o.cancelled_by, at: o.cancelled_at, reason: o.cancel_reason, tone: "amber" });
  push(o.flagged_by, { key: "flagged", label: "Flagged", status: "flagged", signer: o.flagged_by, at: o.flagged_at, reason: o.flag_reason, tone: "amber" });
  push(o.shopify_synced_at, { key: "shopify", label: "Shopify Synced", status: "sync", signer: o.shopify_order_number || "system", at: o.shopify_synced_at, tone: "default", dedupe: true });

  const direct = events.slice();
  const isDup = (s) => direct.some((e) => e.status === s.status && (e.dedupe || near(e.at, s.at)));
  (o.status_history || []).forEach((s) => {
    if (!s || typeof s !== "object") return;
    const auto = !s.by || s.by === "system";
    const reasonText = String(s.reason || "");
    const isRecheck = reasonText.startsWith("↩") || /recheck/i.test(reasonText);
    const isCodeEdit = Array.isArray(s.code_diff) && s.code_diff.length > 0;
    if (!isRecheck && !isCodeEdit && isDup(s)) return;
    if (auto && !s.reason && !s.note) return;
    const stageName = String(stageLabel[s.status] || s.status || "").replace(/_/g, " ");
    events.push({
      key: `stage-${s.status}-${s.at || Math.random()}`,
      label: isCodeEdit ? `${stageName} · Codes changed` : isRecheck ? `${stageName} · Rechecked` : auto ? `${stageName} · Automatic` : `${stageName} · Approved`,
      status: s.status,
      signer: isCodeEdit && s.signature ? s.signature : auto ? "System" : s.by,
      at: s.at,
      reason: isCodeEdit ? (s.note || s.reason) : (s.reason || s.note),
      code_diff: s.code_diff,
      tone: isRecheck || isCodeEdit ? "amber" : "default",
    });
  });
  events.sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime());
  if (events.length > 0) events[0].isCurrent = true;
  return events;
}
